<template>
  <div class="action-buttons" data-test="action-buttons">
    <rate-limit-counter
      v-if="shouldShowRateCounterCircle"
      variation="flashcard-v2"
      class="right-margin"
      data-test="rate-limit-counter"
    />
    <mira-button
      v-if="shouldShowMira"
      class="action-button"
      :asset-id="flashcardId"
      asset-type="V3Flashcard"
      :context-type="contextType"
      data-test="mira-button"
    />
    <template v-if="isPhone">
      <div
        class="more-actions-wrapper"
        data-test="more-actions-wrapper"
        @mouseleave="showMoreMenu = false"
      >
        <circular-icon-button
          class="action-button"
          icon-name="dots"
          icon-variant="181D27"
          data-test="more-actions-button"
          @click="handleMoreActions"
        />
        <div
          v-if="showMoreMenu"
          class="pop-up-menu"
          role="menu"
          aria-label="More actions"
          data-test="pop-up-menu"
        >
          <button
            v-if="!isQuiz"
            class="pop-up-menu-item text-sm font-medium"
            role="menuitem"
            data-test="bookmark-button"
            @click="handleBookmark"
            @touchstart.stop
          >
            <span class="icon-wrapper">
              <icon
                :name="hasBookmark ? 'bookmark-icon-filled' : 'bookmark-icon'"
              />
            </span>
            <span class="text-sm font-medium">Bookmark</span>
          </button>
          <button
            class="pop-up-menu-item text-sm font-medium"
            role="menuitem"
            data-test="share-menu-button"
            @click="handleShare"
            @touchstart.stop
          >
            <span class="icon-wrapper">
              <icon name="share-item" variant="667085" />
            </span>
            <span class="text-sm font-medium">Share</span>
          </button>
          <button
            v-if="showImproveQuestion"
            class="pop-up-menu-item text-sm font-medium"
            role="menuitem"
            data-test="improve-button"
            @click="handleImprove"
            @touchstart.stop
          >
            <span class="icon-wrapper">
              <icon name="flag-outline" variant="667085" />
            </span>
            <span class="text-sm font-medium">Improve</span>
          </button>
        </div>
      </div>
      <share-button
        ref="shareButton"
        :flashcard="flashcard"
        style="display: none;"
        data-test="hidden-share-button"
      ></share-button>
    </template>
    <template v-else>
      <share-button
        :flashcard="flashcard"
        data-test="share-button"
      ></share-button>
      <bookmark-button
        v-if="!isQuiz"
        class="action-button"
        :flashcard="flashcard"
        :category="category"
        data-test="bookmark-button"
      ></bookmark-button>
    </template>
  </div>
</template>

<script>
import BookmarkButton from '@/components/flashcards-v2/fragments/BookmarkButton.vue';
import BookmarksMixin from '@/mixins/bookmarks';
import CircularIconButton from '@/components/ui/CircularIconButton.vue';
import DeviceTypeMixin from '@/mixins/deviceType';
import Icon from '@/components/singletons/icon';
import MiraButton from '@/components/mira/MiraButton.vue';
import RateLimitCounter from '@/components/singletons/rate-limit-counter.vue';
import RateLimitMixin from '@/mixins/rate-limit';
import ShareButton from '@/components/flashcards-v2/fragments/ShareButton.vue';
import AiEventsMixin from '@/mixins/aiEvents';

export default {
  components: {
    BookmarkButton,
    CircularIconButton,
    Icon,
    MiraButton,
    RateLimitCounter,
    ShareButton,
  },
  mixins: [BookmarksMixin, DeviceTypeMixin, RateLimitMixin, AiEventsMixin],
  props: {
    contextType: {
      type: String,
      default: 'home',
    },
    flashcard: {
      type: Object,
      default: () => {},
    },
    category: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showMoreMenu: false,
    };
  },
  computed: {
    shouldShowRateCounterCircle() {
      return this.flashcardIsRateLimited(this.flashcard) && !this.isQotD && this.isPhone;
    },
    isQotD() {
      return this.$route.path.includes('/qotd');
    },
    isQuiz() {
      return this.$route.path.includes('/quizzes');
    },
    flashcardId() {
      return this.flashcard?.id ?? null;
    },
    categoryId() {
      return this.category?.id;
    },
    improveQuestionRoute() {
      let message = '';
      if (this.flashcard?.id) {
        message = `Improve this question: Question ID ${this.flashcard.id}\nEnter your suggestions after this line:\n`;
      }
      return { name: 'support', query: {message: message}};
    },
    showImproveQuestion() {
      const pv = this.$store.getters['productVars/get']['disable_improve_question'];
      const disabled = pv && pv.value === 'true';
      return !disabled;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleMoreActions() {
      this.showMoreMenu = !this.showMoreMenu;
    },
    handleBookmark() {
      this.toggleBookmark();
    },
    handleShare() {
      this.showMoreMenu = false;
      this.$nextTick(() => {
        if (this.$refs.shareButton) {
          this.$refs.shareButton.triggerShare();
        }
      });
    },
    handleImprove() {
      this.showMoreMenu = false;
      this.$router.push(this.improveQuestionRoute);
    },
    handleClickOutside(event) {
      const wrapper = this.$el.querySelector('.more-actions-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        this.showMoreMenu = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .action-buttons {
    margin-right: rem-calc(16);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: rem-calc(8);

    .right-margin {
      margin-right: rem-calc(8);
    }

    .action-button {
      display: grid;
      place-items: center;
      padding: 0;
    }

    .more-actions-wrapper {
      position: relative;
    }

    .pop-up-menu {
      position: absolute;
      top: 40px;
      right: 0;
      background: white;
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-md);
      min-width: rem-calc(160);
      padding: var(--spacing-sm);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      z-index: 1000;

      .pop-up-menu-item {
        display: flex;
        align-items: center;
        cursor: pointer;
        gap: rem-calc(8);
        padding: var(--spacing-sm) var(--spacing-sm);
        margin: var(--spacing-sm) 0;
        border-radius: var(--radius-md);
        border: none;
        background: transparent;
        width: 100%;
        text-align: left;

        .icon-wrapper {
          width: rem-calc(16);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        span:not(.icon-wrapper) {
          color: rgba(24, 34, 48, 1);
        }

        &:hover,
        &:focus {
          background: var(--bg-secondary);
          outline: none;
        }

        &:focus-visible {
          outline: 2px solid var(--fg-brand-primary-600);
          outline-offset: -2px;
        }
      }
    }
  }
</style>
